import axios, { AxiosError } from 'axios';
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import getFacetecCustomization from './customization';
import { FacetecProcessor, FacetecResult, ProcessorMethod } from './facetecProcessor';
import facetecConfigService, { FaceTecStringsLanguages } from './configService';
import Log from '../../../common/logging';
import Result, { CancellableResult } from '../../../common/result';
import { Resolver } from '../../../config';
import { Token } from '../../common/token';
import { CustomError, NetworkError, TechnicalError, RateLimitError } from '../../../common/errors';
import { FacetecIdentifier } from './identifier';

import { EXTERNAL_USER_ID_HEADER, FLOW_ID_HEADER, USER_TOKEN_HEADER } from '../../../common/constants';

export type FacetecRequestOptions = {
  flowId?: string;
  externalUserId?: string;
  authToken?: string;
};

const buildRequestAdditionalHeaders = (requestOptions: FacetecRequestOptions = {}): Record<string, string> => {
  const removeNullAttributes = R.reject(R.isNil);
  return removeNullAttributes({
    [FLOW_ID_HEADER]: requestOptions.flowId,
    [EXTERNAL_USER_ID_HEADER]: requestOptions.externalUserId,
    [USER_TOKEN_HEADER]: requestOptions.authToken,
  }) as Record<string, string>;
};

let isInitializing = false;
let darkMode = false;

function setDarkMode(isDarkModeEnabled: boolean): void {
  darkMode = isDarkModeEnabled;
}

async function initialize(): Promise<boolean> {
  if (isInitializing) {
    return Promise.resolve(false);
  }
  isInitializing = true;

  const { FaceTecSDK } = await import('facetec-sdk');
  const customization = await getFacetecCustomization(darkMode);
  return new Promise<boolean>((resolve) => {
    const config = facetecConfigService.getFacetecConfig();
    let licenseKey = null;
    try {
      licenseKey = JSON.parse(config.licenseKey);
    } catch (error) {
      Log.error('Error parsing license key as JSON', error as Error);
    }
    FaceTecSDK.setResourceDirectory(`${process.env.PUBLIC_URL}/facetec-sdk/resources`);
    FaceTecSDK.setImagesDirectory(`${process.env.PUBLIC_URL}/facetec-sdk/FaceTec_images`);
    FaceTecSDK.setCustomization(customization);
    const initilizeCallback = (initialized: boolean) => {
      if (initialized) {
        // get just the user language code
        const languageCode = navigator.language.split('-')[0];

        FaceTecSDK.configureLocalization(
          FaceTecStringsLanguages[languageCode] !== undefined
            ? FaceTecStringsLanguages[languageCode]
            : FaceTecStringsLanguages.en,
        );
      }
      resolve(initialized);
    };
    if (config.productionMode) {
      FaceTecSDK.initializeInProductionMode(
        licenseKey,
        config.deviceKeyIdentifier,
        config.publicFaceScanEncryptionKey,
        (initialized: boolean) => {
          isInitializing = false;
          initilizeCallback(initialized);
        },
      );
    } else {
      FaceTecSDK.initializeInDevelopmentMode(
        config.deviceKeyIdentifier,
        config.publicFaceScanEncryptionKey,
        (initialized: boolean) => {
          isInitializing = false;
          initilizeCallback(initialized);
        },
      );
    }
  });
}

async function getEnrollmentIdentifier(
  identityAuthId: string,
  requestOptions?: FacetecRequestOptions,
): Promise<Result<CustomError, FacetecIdentifier>> {
  const config = facetecConfigService.getFacetecConfig();
  const serviceConfig = Resolver.getServiceConfig();

  try {
    const response = await axios.request({
      method: 'POST',
      url: `${config.facetecBaseURL}/enrollment/enrollmentId`,
      data: {
        did: identityAuthId,
      },
      retryConfig: {
        retries: serviceConfig.networkRetryConfig.defaultRetries,
      },
      headers: {
        ...buildRequestAdditionalHeaders(requestOptions),
      },
    });

    const { enrollmentIdentifier } = response.data;
    return Result.successful({
      enrollmentIdentifier,
      identityAuthId,
    });
  } catch (error) {
    const statusCode = (error as AxiosError)?.response?.status;

    if (statusCode === 429) {
      return Result.failed(
        new RateLimitError('Rate limit exceeded when retrieving enrollment identifier', error as Error),
      );
    }

    return Result.failed(
      new NetworkError(
        'Unexpected network error retrieving the enrollment identifier',
        statusCode || 500,
        error as Error,
      ),
    );
  }
}

async function enrollmentExists(
  identifier: FacetecIdentifier,
  token: Token,
  requestOptions?: FacetecRequestOptions,
): Promise<Result<CustomError>> {
  const config = facetecConfigService.getFacetecConfig();
  const serviceConfig = Resolver.getServiceConfig();
  const userTokenResult = await token.getToken();
  if (userTokenResult.isFailed()) {
    return Result.noValueFailed(
      new TechnicalError('Error getting token for enrollment exists', userTokenResult.failure()),
    );
  }

  try {
    const { enrollmentIdentifier } = identifier;
    const response = await axios.request({
      method: 'GET',
      url: `${config.facetecBaseURL}/enrollment-3d/${enrollmentIdentifier}`,
      headers: {
        Authorization: `Bearer ${userTokenResult.success()}`,
        ...buildRequestAdditionalHeaders(requestOptions),
      },
      retryConfig: {
        retries: serviceConfig.networkRetryConfig.defaultRetries,
      },
    });

    const { success } = response.data;
    return Result.successful(success || false);
  } catch (error) {
    const statusCode = (error as AxiosError)?.response?.status;
    // If the server returns 404 it means the enrollment doesn't exist, so return a success result with false
    if (statusCode === 404) {
      return Result.successful(false);
    }

    // If the server returns 429 it means rate limit exceeded
    if (statusCode === 429) {
      return Result.failed(
        new RateLimitError('Rate limit exceeded when checking if enrollment exists', error as Error),
      );
    }

    return Result.failed(
      new NetworkError('Unexpected network error determining if enrollment exists', statusCode || 500, error as Error),
    );
  }
}

async function requestSessionToken(requestOptions?: FacetecRequestOptions): Promise<Result<CustomError, string>> {
  const config = facetecConfigService.getFacetecConfig();
  const serviceConfig = Resolver.getServiceConfig();
  const { FaceTecSDK } = await import('facetec-sdk');
  try {
    const response = await axios.request({
      method: 'GET',
      url: `${config.facetecBaseURL}/session-token`,
      headers: {
        'X-Device-Key': config.deviceKeyIdentifier,
        'X-User-Agent': FaceTecSDK.createFaceTecAPIUserAgentString(''),
        ...buildRequestAdditionalHeaders(requestOptions),
      },
      retryConfig: {
        retries: serviceConfig.networkRetryConfig.defaultRetries,
      },
    });
    const { sessionToken } = response.data;
    if (typeof sessionToken !== 'string') {
      return Result.failed(
        new TechnicalError(`Unexpected result creating session token: ${JSON.stringify(response.data)}`),
      );
    }
    return Result.successful(sessionToken);
  } catch (error) {
    const statusCode = (error as AxiosError)?.response?.status;

    // If the server returns 429 it means rate limit exceeded
    if (statusCode === 429) {
      return Result.failed(new RateLimitError('Rate limit exceeded when requesting session token', error as Error));
    }

    return Result.failed(
      new NetworkError('Unexpected network error creating session token', statusCode || 500, error as Error),
    );
  }
}

async function processFacetecSession(processor: FacetecProcessor): Promise<FacetecResult> {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise<FacetecResult>((resolve) => processor.startSession(resolve));
}

async function enroll(
  identifier: FacetecIdentifier,
  token: Token,
  requestOptions?: FacetecRequestOptions,
): Promise<FacetecResult> {
  const sessionTokenResult = await requestSessionToken(requestOptions);
  if (sessionTokenResult.isFailed()) {
    return CancellableResult.failed(sessionTokenResult.failure());
  }

  const userTokenResult = await token.getToken();
  if (userTokenResult.isFailed()) {
    return CancellableResult.failed(
      new TechnicalError('Error getting token for enrollment', userTokenResult.failure()),
    );
  }

  const additionalHeaders = {
    Authorization: `Bearer ${userTokenResult.success()}`,
    ...buildRequestAdditionalHeaders(requestOptions),
  };
  const processor = new FacetecProcessor(
    sessionTokenResult.success(),
    identifier,
    ProcessorMethod.ENROLLMENT,
    null,
    additionalHeaders,
  );
  return processFacetecSession(processor);
}

async function authenticate(
  identifier: FacetecIdentifier,
  token: Token,
  requestOptions?: FacetecRequestOptions,
): Promise<FacetecResult> {
  const sessionTokenResult = await requestSessionToken(requestOptions);
  if (sessionTokenResult.isFailed()) {
    return CancellableResult.failed(sessionTokenResult.failure());
  }

  const userTokenResult = await token.getToken();
  if (userTokenResult.isFailed()) {
    return CancellableResult.failed(
      new TechnicalError('Error getting token for authentication', userTokenResult.failure()),
    );
  }

  const additionalHeaders = {
    Authorization: `Bearer ${userTokenResult.success()}`,
    ...buildRequestAdditionalHeaders(requestOptions),
  };
  const processor = new FacetecProcessor(
    sessionTokenResult.success(),
    identifier,
    ProcessorMethod.AUTHENTICATE,
    null,
    additionalHeaders,
  );
  return processFacetecSession(processor);
}

async function faceIdEnroll(
  identifier: FacetecIdentifier,
  token: Token,
  requestOptions?: FacetecRequestOptions,
): Promise<FacetecResult> {
  const sessionTokenResult = await requestSessionToken(requestOptions);
  if (sessionTokenResult.isFailed()) {
    return CancellableResult.failed(sessionTokenResult.failure());
  }

  const userTokenResult = await token.getToken();
  if (userTokenResult.isFailed()) {
    return CancellableResult.failed(
      new TechnicalError('Error getting token for uniqueness enrollment', userTokenResult.failure()),
    );
  }

  const additionalHeaders = {
    Authorization: `Bearer ${userTokenResult.success()}`,
    ...buildRequestAdditionalHeaders(requestOptions),
  };
  const processor = new FacetecProcessor(
    sessionTokenResult.success(),
    identifier,
    ProcessorMethod.FACE_ID_ENROLLMENT,
    token,
    additionalHeaders,
  );
  return processFacetecSession(processor);
}

async function faceIdAuthenticate(
  identifier: FacetecIdentifier,
  token: Token,
  requestOptions?: FacetecRequestOptions,
): Promise<FacetecResult> {
  const sessionTokenResult = await requestSessionToken(requestOptions);
  if (sessionTokenResult.isFailed()) {
    return CancellableResult.failed(sessionTokenResult.failure());
  }

  const userTokenResult = await token.getToken();
  if (userTokenResult.isFailed()) {
    return CancellableResult.failed(
      new TechnicalError('Error getting token for uniqueness enrollment', userTokenResult.failure()),
    );
  }

  const additionalHeaders = {
    Authorization: `Bearer ${userTokenResult.success()}`,
    ...buildRequestAdditionalHeaders(requestOptions),
  };
  const processor = new FacetecProcessor(
    sessionTokenResult.success(),
    identifier,
    ProcessorMethod.FACE_ID_AUTHENTICATE,
    token,
    additionalHeaders,
  );
  return processFacetecSession(processor);
}

// creates a function that generates random Facetec identifier
// identityAuthId should be a random string
// enrollmentIdentifier should be a hash of identityAuthId

function createFacetecIdentifierGenerator(): FacetecIdentifier {
  const identityAuthId = uuidv4();
  return {
    identityAuthId,
    enrollmentIdentifier: identityAuthId,
  };
}

async function search(requestOptions?: FacetecRequestOptions): Promise<FacetecResult> {
  const sessionTokenResult = await requestSessionToken(requestOptions);
  if (sessionTokenResult.isFailed()) {
    return CancellableResult.failed(sessionTokenResult.failure());
  }

  const additionalHeaders = buildRequestAdditionalHeaders(requestOptions);
  const identifier = createFacetecIdentifierGenerator();
  const processor = new FacetecProcessor(
    sessionTokenResult.success(),
    identifier,
    ProcessorMethod.SEARCH,
    null,
    additionalHeaders,
  );
  return processFacetecSession(processor);
}

export default {
  initialize,
  enrollmentExists,
  requestSessionToken,
  enroll,
  authenticate,
  faceIdEnroll,
  faceIdAuthenticate,
  getEnrollmentIdentifier,
  search,
  setDarkMode,
};
